import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import {
  FaEdit,
  FaExternalLinkAlt,
  FaSyncAlt,
  FaTrashAlt,
} from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import {
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from 'reactstrap';
import styled from 'styled-components';
import { archivosFiles } from '../../../../services/api';
import {
  deleteNewsletterAction,
  getAllNewsletterAction,
  setActivoNewsletterAction,
} from '../../../../store/actions';

const Container = styled.div`
  width: 80vw;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem;
  background: #f8f9fa;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;

  h4 {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 700;
    color: #212529;
  }

  span {
    font-size: 0.8rem;
    color: #6c757d;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ImageStack = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
`;

const ImagePreview = styled.div`
  width: ${({ $size }) => $size ?? '52px'};
  height: ${({ $size }) => $size ?? '52px'};
  border-radius: 6px;
  overflow: hidden;
  border: 2px solid ${({ $border }) => $border ?? '#dee2e6'};
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  flex-shrink: 0;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ImageLabel = styled.span`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  font-size: 0.55rem;
  font-weight: 700;
  text-align: center;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  padding: 1px 0;
  letter-spacing: 0.04em;
`;

const LinkCell = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.78rem;
  color: #0d6efd;
  text-decoration: none;
  word-break: break-all;
  max-width: 220px;

  &:hover {
    text-decoration: underline;
    color: #0a58ca;
  }

  svg {
    flex-shrink: 0;
    font-size: 0.65rem;
  }
`;

const NoLink = styled.span`
  font-size: 0.78rem;
  color: #adb5bd;
  font-style: italic;
`;

const DimensionGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr 1fr;
  gap: 0.15rem 0.5rem;
  font-size: 0.72rem;
  min-width: 130px;

  .head {
    font-weight: 700;
    color: #868e96;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    font-size: 0.62rem;
  }

  .label {
    color: #495057;
    font-weight: 600;
  }

  .value {
    text-align: center;
    color: #212529;
    background: #e9ecef;
    border-radius: 4px;
    padding: 1px 4px;
    font-weight: 600;
  }
`;

const AppearanceCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 110px;
`;

const ColorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
`;

const ColorSwatch = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 4px;
  border: 1px solid #ced4da;
  background: ${({ $color }) => $color || '#FFFFFF'};
  background-image: ${({ $transparent }) =>
    $transparent
      ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)'
      : 'none'};
  background-size: ${({ $transparent }) => ($transparent ? '8px 8px' : 'auto')};
  flex-shrink: 0;
`;

const MetaText = styled.span`
  font-size: 0.72rem;
  color: #495057;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.35rem;
  align-items: center;
`;

const DeletePreview = styled.div`
  margin-top: 1rem;
  padding: 0.75rem;
  border-radius: 8px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  img {
    width: 64px;
    height: 64px;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid #dee2e6;
  }

  div {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    font-size: 0.85rem;

    strong {
      color: #212529;
    }

    span {
      color: #6c757d;
      font-size: 0.78rem;
    }
  }
`;

const getImageUrl = (file) =>
  file ? `${archivosFiles}/${file.folder}/${file.nombre}` : null;

const truncateLink = (link, max = 42) => {
  if (!link) return '';
  return link.length > max ? `${link.slice(0, max)}…` : link;
};

const ListadoNewletter = () => {
  const dispatch = useDispatch();
  const tiposBoletinState = useSelector((state) => state.tiposBoletin);
  const newsletterState = useSelector((state) => state.newsletter);

  const [loadingRefresh, setLoadingRefresh] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [isModalDelete, setIsModalDelete] = useState(false);
  const [dataDelete, setDataDelete] = useState(null);

  const tipoBoletinId = tiposBoletinState.activo?.id;

  const fetchNewsletter = useCallback(
    async (page = 1, limit) => {
      if (!tipoBoletinId) return;
      await getAllNewsletterAction(dispatch, {
        page,
        limit: limit ?? newsletterState.meta?.itemsPerPage ?? 50,
        tipoBoletin: tipoBoletinId,
      });
    },
    [dispatch, newsletterState.meta?.itemsPerPage, tipoBoletinId],
  );

  const getAllNewsletter = useCallback(async () => {
    setLoadingRefresh(true);
    await fetchNewsletter(1);
    setLoadingRefresh(false);
  }, [fetchNewsletter]);

  useEffect(() => {
    getAllNewsletter();
  }, [getAllNewsletter]);

  const toggle = () => {
    setIsModalDelete(false);
    setDataDelete(null);
  };

  const handleEdit = useCallback(
    (row, e) => {
      e?.stopPropagation?.();
      setActivoNewsletterAction(dispatch, row);
    },
    [dispatch],
  );

  const handleDeleteClick = useCallback((row, e) => {
    e.stopPropagation();
    setDataDelete(row);
    setIsModalDelete(true);
  }, []);

  const columns = useMemo(
    () => [
      {
        name: 'ID',
        width: '64px',
        selector: (row) => row.id,
        sortable: true,
        center: true,
      },
      {
        name: 'Vista previa',
        width: '130px',
        center: true,
        cell: (row) => (
          <ImageStack>
            <ImagePreview $size="52px" $border="#495057">
              <img
                alt={`GIF ${row.id}`}
                src={getImageUrl(row.gif) ?? '../../img/picture.png'}
              />
              <ImageLabel>GIF</ImageLabel>
            </ImagePreview>
            <ImagePreview
              $size="52px"
              $border={row.png ? '#0d6efd' : '#dee2e6'}
            >
              {row.png ? (
                <img alt={`PNG ${row.id}`} src={getImageUrl(row.png)} />
              ) : (
                <NoLink style={{ fontSize: '0.6rem' }}>—</NoLink>
              )}
              <ImageLabel>PNG</ImageLabel>
            </ImagePreview>
          </ImageStack>
        ),
      },
      {
        name: 'Link',
        minWidth: '180px',
        grow: 1.2,
        selector: (row) => row.link,
        sortable: true,
        wrap: true,
        cell: (row) =>
          row.link && row.link !== '#' ? (
            <LinkCell
              href={row.link}
              target="_blank"
              rel="noopener noreferrer"
              title={row.link}
              onClick={(e) => e.stopPropagation()}
            >
              <FaExternalLinkAlt />
              {truncateLink(row.link)}
            </LinkCell>
          ) : (
            <NoLink>Sin link</NoLink>
          ),
      },
      {
        name: 'Dimensiones (%)',
        width: '150px',
        cell: (row) => (
          <DimensionGrid>
            <span />
            <span className="head">Desk</span>
            <span className="head">Móvil</span>
            <span className="label">GIF</span>
            <span className="value">{row.ancho ?? 85}</span>
            <span className="value">{row.anchoMovil ?? 90}</span>
            <span className="label">PNG</span>
            <span className="value">{row.anchoImg2 ?? 85}</span>
            <span className="value">{row.anchoMovilImg2 ?? 90}</span>
          </DimensionGrid>
        ),
      },
      {
        name: 'Posición',
        width: '88px',
        selector: (row) => row.posicion,
        sortable: true,
        center: true,
        cell: (row) => (
          <Badge color="secondary" pill style={{ fontSize: '0.75rem' }}>
            {row.posicion ?? 1}
          </Badge>
        ),
      },
      {
        name: 'Apariencia',
        width: '140px',
        cell: (row) => {
          const bg = row.backgroundColor ?? '#FFFFFF';
          const isTransparent = bg.toLowerCase() === 'transparent';
          return (
            <AppearanceCell>
              <ColorRow>
                <ColorSwatch $color={bg} $transparent={isTransparent} />
                <MetaText>{bg}</MetaText>
              </ColorRow>
              <MetaText>
                ↑ {row.marginTop ?? '0px'} · ↓{' '}
                {row.paddingBottom ?? row.heightBottom ?? '3em'}
              </MetaText>
            </AppearanceCell>
          );
        },
      },
      {
        name: 'Estado',
        width: '100px',
        selector: (row) => row.public,
        sortable: true,
        center: true,
        cell: (row) => (
          <Badge
            color={row.public ? 'success' : 'danger'}
            pill
            style={{ fontSize: '0.72rem' }}
          >
            {row.public ? 'Activo' : 'Bloqueado'}
          </Badge>
        ),
      },
      {
        name: 'Acciones',
        width: '100px',
        center: true,
        cell: (row) => (
          <ActionButtons>
            <Button
              size="sm"
              color="primary"
              outline
              title="Editar"
              onClick={(e) => handleEdit(row, e)}
            >
              <FaEdit />
            </Button>
            <Button
              size="sm"
              color="danger"
              outline
              title="Eliminar"
              onClick={(e) => handleDeleteClick(row, e)}
            >
              <FaTrashAlt />
            </Button>
          </ActionButtons>
        ),
      },
    ],
    [handleEdit, handleDeleteClick],
  );

  const paginationComponentOptions = {
    rowsPerPageText: 'Filas por página',
    rangeSeparatorText: 'de',
    selectAllRowsItem: true,
    selectAllRowsItemText: 'Todos',
  };

  const customStyles = {
    table: { style: { width: '100%' } },
    tableWrapper: { style: { width: '100%', overflowX: 'auto' } },
    headRow: {
      style: {
        backgroundColor: '#343a40',
        color: '#fff',
        fontSize: '0.78rem',
        fontWeight: '600',
        minHeight: '48px',
      },
    },
    headCells: {
      style: { paddingLeft: '12px', paddingRight: '12px' },
    },
    cells: {
      style: {
        paddingLeft: '12px',
        paddingRight: '12px',
        fontSize: '0.82rem',
      },
    },
    rows: {
      style: {
        minHeight: '80px',
        '&:hover': { backgroundColor: '#eef2f7' },
      },
      stripedStyle: { backgroundColor: '#fcfcfd' },
    },
  };

  const handlePageChange = async (page) => {
    setLoadingData(true);
    await fetchNewsletter(page);
    setLoadingData(false);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setLoadingData(true);
    await fetchNewsletter(page, newPerPage);
    setLoadingData(false);
  };

  const totalItems =
    newsletterState.meta?.totalItems ?? newsletterState.all.length;

  return (
    <Container>
      <PageHeader>
        <HeaderInfo>
          <h4>{tiposBoletinState.activo?.nombre ?? 'Newsletter'}</h4>
          <span>
            {totalItems} registro{totalItems !== 1 ? 's' : ''} · tipo boletín #
            {tipoBoletinId}
          </span>
        </HeaderInfo>
        <HeaderActions>
          <Button
            color="light"
            size="sm"
            disabled={loadingRefresh}
            onClick={getAllNewsletter}
          >
            {loadingRefresh ? (
              <>
                <Spinner size="sm" /> Actualizando…
              </>
            ) : (
              <>
                <FaSyncAlt style={{ marginRight: 6 }} />
                Actualizar
              </>
            )}
          </Button>
        </HeaderActions>
      </PageHeader>

      <DataTable
        columns={columns}
        data={newsletterState.all}
        pagination
        paginationComponentOptions={paginationComponentOptions}
        dense
        striped
        pointerOnHover
        highlightOnHover
        customStyles={customStyles}
        fixedHeader
        fixedHeaderScrollHeight="560px"
        responsive
        noDataComponent={
          <div style={{ padding: '2rem', color: '#6c757d' }}>
            No hay registros para este newsletter.
          </div>
        }
        onRowClicked={(row, event) => {
          if (event.target.closest('button') || event.target.closest('a')) {
            return;
          }
          setActivoNewsletterAction(dispatch, row);
        }}
        paginationPerPage={newsletterState.meta?.itemsPerPage ?? 50}
        paginationRowsPerPageOptions={[10, 30, 50]}
        paginationTotalRows={totalItems}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        paginationServer
        progressPending={loadingData}
      />

      <Modal isOpen={isModalDelete} toggle={toggle} centered>
        <ModalHeader toggle={toggle}>Eliminar registro</ModalHeader>
        <ModalBody>
          ¿Seguro que deseas eliminar este bloque del newsletter?
          {dataDelete && (
            <DeletePreview>
              <img
                alt="Preview"
                src={getImageUrl(dataDelete.gif) ?? '../../img/picture.png'}
              />
              <div>
                <strong>ID #{dataDelete.id}</strong>
                <span>Posición: {dataDelete.posicion}</span>
                <span>
                  Link:{' '}
                  {dataDelete.link && dataDelete.link !== '#'
                    ? truncateLink(dataDelete.link, 60)
                    : 'Sin link'}
                </span>
              </div>
            </DeletePreview>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" size="sm" onClick={toggle}>
            Cancelar
          </Button>
          <Button
            color="danger"
            size="sm"
            onClick={async () => {
              await deleteNewsletterAction(dispatch, dataDelete.id, {
                tipoBoletin: tipoBoletinId,
              });
              toggle();
            }}
          >
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default ListadoNewletter;
